import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@mui/material';

import { Control, FormCard } from '../../../../components';
import { CARD_CATEGORIES } from '../../../../shared/cardConfig/constants';
import {
  getAddressConfigByCategory as getAddressConfigByCategoryRaw,
  getAddressFieldConfig as getAddressFieldConfigRaw
} from '../../../../shared/cardConfig/helpers';
import { PAYER_ADDRESS_FIELDS } from '../../../../shared/fieldConfig/constants';
import { clearFieldError } from '../../../../store/fieldError';
import { CONTROL_TYPE } from '../../../../utils/constants';
import { VALIDATION } from '../../../../utils/validation';
import AddTaxNumberButton from '../../../AddTaxNumberButton';
import { PaymentSlipEmail, XmlEmail } from '../../company/SoldToAddressCard';
import type {
  AddressFieldConfig,
  CardDispatch,
  CardErrorMustContain,
  CardField,
  CardFieldComponent,
  CardFieldProps,
  CardStore,
  TFunc
} from '../../types';
const category = CARD_CATEGORIES.payer;

// These helpers are authored in JS with JSDoc types that are stricter than
// these call sites' runtime usage; give them explicit (mostly optional)
// argument types and concrete result shapes. The JS inference for
// `getAddressFieldConfig` is wrong (it reports a boolean return), so an
// `unknown` hop is required to reach the real config shape.
const getAddressFieldConfig = getAddressFieldConfigRaw as unknown as (
  field: string,
  t: TFunc,
  disabled?: boolean,
  maxCharacters?: number | null,
  category?: string,
  isOptional?: boolean,
  controlType?: string
) => AddressFieldConfig;
const getAddressConfigByCategory = getAddressConfigByCategoryRaw as (
  category: string,
  t: TFunc,
  store?: CardStore,
  isCardOptional?: boolean,
  multiShipToIndex?: number,
  disabled?: boolean
) => AddressFieldConfig[];

interface PayerAddressCardProps {
  isReview?: boolean;
  fields: CardField[];
  store: CardStore;
  errorMustContain?: CardErrorMustContain;
}

function TaxNumber1({ t, isReview, disableAllFields, store, required }: CardFieldProps) {
  const { customer } = store;
  const { [category]: { address: { naturalPerson } = {} } = {}, region = '', countryCode = '' } = customer;
  const isTaxNumber1Required = region.toLowerCase() === 'latam' && countryCode === 'MX' ? !naturalPerson : false;
  const field = 'taxNumber1';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    required: required || isTaxNumber1Required,
    label: t('address.taxNumber1.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled: isReview || disableAllFields,
    placeholder: t('address.taxNumber1.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber2({ t, disabled }: CardFieldProps) {
  const field = 'taxNumber2';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber2.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber2.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber3({ t, disabled }: CardFieldProps) {
  const field = 'taxNumber3';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber3.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber3.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber4({ t, disabled }: CardFieldProps) {
  const field = 'taxNumber4';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber4.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber4.placeholder')
  };

  return <Control {...config} />;
}

function PhoneNumber1({ disabled, t, required }: CardFieldProps) {
  const field = 'telephone1';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    required: required,
    label: t(`address.telephone1.label`),
    placeholder: t(`address.telephone1.placeholder`),
    validations: [{ type: VALIDATION.number }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled
  };

  return <Control {...config} />;
}

function PhoneNumber2({ disabled, t }: CardFieldProps) {
  const field = 'telephone2';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t(`address.telephone2.label`),
    placeholder: t(`address.telephone2.placeholder`),
    validations: [{ type: VALIDATION.number }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled
  };

  return <Control {...config} />;
}

function Emails({ disabled, t, required }: CardFieldProps) {
  const field = 'email';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    field: 'email',
    category,
    label: t('address.email.label'),
    type: CONTROL_TYPE.inputWithTags,
    required: required,
    validations: [
      { type: VALIDATION.array, param: [{ type: VALIDATION.email }] },
      { type: VALIDATION.maxCharacters, param: 255 }
    ],
    onAddTag: ({ store, dispatch }: { store: CardStore; dispatch: CardDispatch }) => {
      // If there is an error on the parent ('invoice delivery') but an email has been entered, clear the error on the parent
      if (typeof store.fieldError[name] === 'string') {
        dispatch(clearFieldError(name));
      }
    },
    placeholder: t('address.email.placeholder'),
    disabled
  };
  return <Control {...config} />;
}

function DnbNumber({ t, isReview, disableAllFields }: CardFieldProps) {
  const field = 'dnbNumber';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.dnbNumber.label'),
    validations: [{ type: VALIDATION.numberOfDigits, param: 9 }],
    disabled: isReview || disableAllFields,
    placeholder: t('address.dnbNumber.placeholder'),
    footer: (
      <Typography
        variant="body2"
        sx={{
          mt: '4px',
          '& a': {
            fontWeight: '400 !important',
            fontSize: '12px !important',
            lineHeight: '16px !important'
          }
        }}
      >
        {t('address.dnbNumber.footer')}
        <a className="sprout-link base" href="https://www.dnb.com/duns-number/lookup.html">
          {' '}
          https://www.dnb.com/duns-number/lookup.html
        </a>
      </Typography>
    )
  };

  return <Control {...config} />;
}

function Address({ disableAllFields, t, hideSameAs, store, isReview, hideSameAsBillTo }: CardFieldProps) {
  const field = 'address';
  const name = `${category}.${field}`;
  const {
    customer: { region = '', countryCode = '' }
  } = store;
  const isDisable = disableAllFields || isReview;
  let cardSpecificFields: AddressFieldConfig[];

  switch (region.toLowerCase()) {
    case 'latam':
      if (countryCode.toLowerCase() === 'mx') {
        cardSpecificFields = [
          getAddressFieldConfig('naturalPerson', t, isDisable, null, category, false, CONTROL_TYPE.checkbox),
          getAddressFieldConfig('legalName', t, isDisable, 105, category, true),
          getAddressFieldConfig('tradeDbaName', t, isDisable, 35, category)
        ];
      } else {
        cardSpecificFields = [
          getAddressFieldConfig('legalName', t, isDisable, 105, category, true),
          getAddressFieldConfig('tradeDbaName', t, isDisable, 35, category)
        ];
      }
      break;
    case 'emea':
      cardSpecificFields = [
        getAddressFieldConfig('tradeDbaName', t, isDisable, 35, category, true),
        getAddressFieldConfig('legalName', t, isDisable, 105, category, true)
      ];
      break;
    default:
      cardSpecificFields = [
        getAddressFieldConfig('tradeDbaName', t, isDisable, 35, category),
        getAddressFieldConfig('legalName', t, isDisable, 105, category, true)
      ];
      break;
  }

  const config = {
    disabled: isDisable,
    category,
    name,
    showBillTo: hideSameAsBillTo ? false : true,
    showSoldTo: true,
    showNameFields: true,
    nameFieldPrefix: 'Payer',
    showCareOfNameField: false,
    type: CONTROL_TYPE.address,
    hideSameAs,
    label: t(`financial.${category}.label`),
    addressFields: [...cardSpecificFields, ...getAddressConfigByCategory('payer', t, store)]
  };

  return <Control {...config} />;
}

const controlMap: Record<string, CardFieldComponent> = {
  [PAYER_ADDRESS_FIELDS.address]: Address,
  [PAYER_ADDRESS_FIELDS.taxNumber1]: TaxNumber1,
  [PAYER_ADDRESS_FIELDS.taxNumber2]: TaxNumber2,
  [PAYER_ADDRESS_FIELDS.taxNumber3]: TaxNumber3,
  [PAYER_ADDRESS_FIELDS.taxNumber4]: TaxNumber4,
  [PAYER_ADDRESS_FIELDS.dnbNumber]: DnbNumber,
  [PAYER_ADDRESS_FIELDS.paymentSlipEmail]: PaymentSlipEmail,
  [PAYER_ADDRESS_FIELDS.xmlEmail]: XmlEmail,
  [PAYER_ADDRESS_FIELDS.email]: Emails,
  [PAYER_ADDRESS_FIELDS.telephone1]: PhoneNumber1,
  [PAYER_ADDRESS_FIELDS.telephone2]: PhoneNumber2
};

interface RenderTaxNumbersArgs {
  currentTaxNumber: number;
  TotalTaxNumberCount: number;
  disableTaxButton: number;
  fields: CardField[];
  controlMap: Record<string, CardFieldComponent>;
  t: TFunc;
  isReview?: boolean;
  props: PayerAddressCardProps;
  handleAddTaxNumber: (setCurrentTaxNumber?: (value: number) => void) => void;
  setCurrentTaxNumber: (value: number) => void;
}

function renderTaxNumbers({
  currentTaxNumber,
  TotalTaxNumberCount,
  disableTaxButton,
  fields,
  controlMap,
  t,
  isReview,
  props,
  handleAddTaxNumber,
  setCurrentTaxNumber // Receive setCurrentTaxNumber as a prop
}: RenderTaxNumbersArgs) {
  return fields.map(({ key, ...fieldProps }: CardField) => {
    const Component = controlMap[key];
    if (key.startsWith('taxNumber')) {
      const index = parseInt(key.slice(-1)) - 1;
      if (index < currentTaxNumber) {
        return (
          <>
            <Component t={t} disabled={isReview} {...fieldProps} {...props} key={key} />
            {!window.location.href.includes('review') &&
              currentTaxNumber === index + 1 &&
              currentTaxNumber !== TotalTaxNumberCount && (
                <AddTaxNumberButton
                  disabled={!(disableTaxButton + 1 > currentTaxNumber)}
                  onClick={() => handleAddTaxNumber(setCurrentTaxNumber)}
                  t={t}
                />
              )}
          </>
        );
      }
    } else {
      return <Component t={t} disabled={isReview} {...fieldProps} {...props} key={key} />;
    }
    return <></>;
  });
}

function PayerAddressCard(props: PayerAddressCardProps) {
  const { t } = useTranslation();
  const { fields, store, errorMustContain, isReview } = props;
  const {
    customer: {
      payer: { address }
    }
  } = store;
  const TotalTaxNumberCount = fields.filter(obj => obj.key.startsWith('taxNumber')).length;
  const backendTaxNumberCount = Object.keys(address).filter(key => key.startsWith('taxNumber')).length;
  const [currentTaxNumber, setCurrentTaxNumber] = useState(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 1);
  const [disableTaxButton, setDisableTaxButton] = useState(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 0);
  useEffect(() => {
    const backendTaxNumberCount = Object.keys(address).filter(key => key.startsWith('taxNumber')).length;
    setDisableTaxButton(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 0);
    setCurrentTaxNumber(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 1);
  }, [address, setCurrentTaxNumber]);
  const cardProps = {
    cardId: category,
    title: t('financial.payer.title'),
    subtitle: t('financial.payer.subtitle'),
    image: '/food/onboarding/assets/Payer.svg',
    errorMustContain,
    isReview
  };

  const handleAddTaxNumber = () => {
    if (disableTaxButton + 1 > currentTaxNumber) {
      setCurrentTaxNumber(prevNum => prevNum + 1);
    }
  };

  return (
    <FormCard {...cardProps}>
      {renderTaxNumbers({
        currentTaxNumber,
        TotalTaxNumberCount,
        disableTaxButton,
        fields,
        controlMap,
        t,
        isReview,
        props,
        handleAddTaxNumber, // Pass handleAddTaxNumber as a prop
        setCurrentTaxNumber // Pass setCurrentTaxNumber as a prop
      })}
    </FormCard>
  );
}

export default PayerAddressCard;
